import React, { useState, useRef, useEffect } from "react";
import { X, Send, Loader2, AlertCircle } from "lucide-react";

const ImagePreviewModal = ({
  isOpen,
  onClose,
  imageFile,
  imagePreviewUrl,
  onSend,
  isUploading = false,
  caption = "",
  onCaptionChange,
}) => {
  const [captionError, setCaptionError] = useState("");
  const mobileTextareaRef = useRef(null);
  const desktopTextareaRef = useRef(null);

  // Auto focus on caption input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        if (window.innerWidth < 640) {
          // Mobile
          mobileTextareaRef.current?.focus();
        } else {
          // Desktop
          desktopTextareaRef.current?.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = () => {
    // Validate caption is required
    if (!caption.trim()) {
      setCaptionError("Caption is required");
      return;
    }

    setCaptionError("");
    onSend(caption.trim());
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && caption.trim()) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCaptionChange = (e) => {
    if (onCaptionChange) {
      onCaptionChange(e.target.value);
    }
    // Clear error when user starts typing
    if (captionError && e.target.value.trim()) {
      setCaptionError("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Mobile: Full screen layout */}
      <div className="sm:hidden h-full flex flex-col bg-white dark:bg-gray-800">
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Send Image
          </h3>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 transition-colors p-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Image Preview - Flex grow */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 flex items-center justify-center bg-black">
            <img
              src={imagePreviewUrl}
              alt="Preview"
              className="max-w-full max-h-full object-contain"
            />
          </div>

          {/* File Info */}
          <div className="p-3 bg-gray-50 dark:bg-gray-900 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">{imageFile?.name}</span>
            {imageFile?.size && (
              <span className="ml-2">
                ({(imageFile.size / (1024 * 1024)).toFixed(2)} MB)
              </span>
            )}
          </div>
        </div>

        {/* Caption Input - Fixed bottom area */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Caption <span className="text-red-500">*</span>
          </label>
          <textarea
            ref={mobileTextareaRef}
            value={caption}
            onChange={handleCaptionChange}
            onKeyPress={handleKeyPress}
            placeholder="Enter a caption for this image..."
            className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
              captionError
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
            }`}
            rows="2"
            disabled={isUploading}
          />
          {captionError && (
            <div className="flex items-center mt-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 mr-1" />
              {captionError}
            </div>
          )}
        </div>

        {/* Action Buttons - Stacked */}
        <div className="p-4 space-y-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <button
            type="button"
            onClick={handleSend}
            disabled={isUploading || !caption.trim()}
            className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Send
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isUploading}
            className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-base font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
      {/* Desktop: Original modal layout */}
      {/* Desktop: Original modal layout */}
      <div className="hidden sm:flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                Send Image
              </h3>
              <button
                onClick={onClose}
                disabled={isUploading}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Image Preview */}
            <div className="mb-4">
              <div className="relative bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                <img
                  src={imagePreviewUrl}
                  alt="Preview"
                  className="w-full max-h-64 sm:max-h-80 object-contain"
                />
              </div>

              {/* File Info */}
              <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">{imageFile?.name}</span>
                {imageFile?.size && (
                  <span className="ml-2">
                    ({(imageFile.size / (1024 * 1024)).toFixed(2)} MB)
                  </span>
                )}
              </div>
            </div>

            {/* Caption Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Caption <span className="text-red-500">*</span>
              </label>
              <textarea
                ref={desktopTextareaRef}
                value={caption}
                onChange={handleCaptionChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter a caption for this image..."
                className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  captionError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                }`}
                rows="3"
                disabled={isUploading}
              />
              {captionError && (
                <div className="flex items-center mt-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {captionError}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSend}
              disabled={isUploading || !caption.trim()}
              className="w-full inline-flex justify-center items-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:mr-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
      );
    </div>
  );
};

export default ImagePreviewModal;
